import { useState, useEffect } from 'react';
import { useSplash } from '../../../contexts/SplashContext';
import {Tab, Nav} from 'react-bootstrap';
import JobOrderLpoPrint from './JobOrderLpoPrint';
import { useSearchParams } from 'react-router-dom';
import joborderlpoService from '../../../services/joborderlpoService';

const JOB_ORDER_LPO_PREVIEW_CSS_ID = 'job-order-lpo-preview-print-css';

const JobOrderLpoPreview = ({ data, isLoading }) => {
    const [searchParams] = useSearchParams();
    const preview = searchParams.get('preview');
    const splashData = useSplash();
    const languages = splashData?.languages || [];
    const defaultLanguage = languages.find(lang => lang.default)?.code || languages[0]?.code || 'en';
    const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);
    const [activeTab, setActiveTab] = useState('trouser');
    const [showPrintModal, setShowPrintModal] = useState(false);

    const selectedLang = languages.find(l => l.code === selectedLanguage);

    useEffect(() => {
        const href = `${process.env.PUBLIC_URL || ''}/css/job_order_print.css`;
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        link.id = JOB_ORDER_LPO_PREVIEW_CSS_ID;
        document.head.appendChild(link);
        return () => {
            const el = document.getElementById(JOB_ORDER_LPO_PREVIEW_CSS_ID);
            if (el) el.remove();
        };
    }, []);

    // Helper function to get translated text
    const getTranslatedText = (textObj, langCode = selectedLanguage) => {
        if (!textObj) return '';
        if (typeof textObj === 'string') return textObj;
        return textObj[langCode] || textObj[defaultLanguage] || Object.values(textObj)[0] || '';
    };

    // Helper function to format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    // Helper function to format currency
    const formatCurrency = (amount) => {
        if (!amount) return '0.00';
        return parseFloat(amount).toFixed(2);
    };


    useEffect(() => {
        // make dure data is loaded
        if (!data) 
            return;
        
        if (preview) 
            setShowPrintModal(true);
    }, [preview, data]);

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="text-center p-4">
                <h5>No data available</h5>
            </div>
        );
    }

    const handlePrint = () => {
        setShowPrintModal(true);
    };

    const handleClosePrintModal = () => {
        setShowPrintModal(false);
    };

    return (
        <div className="h-100 d-flex flex-column print-content">

            <div className="d-flex justify-content-between mb-2">
                <div className="d-flex justify-content-start">
                    <h5 className="mb-0">Job Order Details</h5>
                </div>
                <div className="d-flex justify-content-end">
                    <button 
                        className="btn btn-primary btn-sm no-print"
                        onClick={handlePrint}
                        style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                    >
                        <i className="fas fa-print"></i>
                        Print Preview
                    </button>
                </div>
            </div>
            
            <div className="flex-grow-1">
                <div className="row">
                    <div className="col-md-4">
                        <div className="mb-0">
                            <label className="form-label fw-bold mb-0">Order Number:</label>
                            <div className="form-control-plaintext">{data.order_number || 'N/A'}</div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="mb-0">
                            <label className="form-label fw-bold mb-0">LPO Number:</label>
                            <div className="form-control-plaintext">{data.lpo_number || 'N/A'}</div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="mb-0">
                            <label className="form-label fw-bold mb-0">Branch Name:</label>
                            <div className="form-control-plaintext">{data.branch_name || 'N/A'}</div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="mb-0">
                            <label className="form-label fw-bold mb-0">Employee Name:</label>
                            <div className="form-control-plaintext">{data.employee_name || 'N/A'}</div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-4">
                        <div className="mb-0">
                            <label className="form-label fw-bold mb-0">Order Date:</label>
                            <div className="form-control-plaintext">{formatDate(data.order_date)}</div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="mb-0">
                            <label className="form-label fw-bold mb-0">Trial Date:</label>
                            <div className="form-control-plaintext">{formatDate(data.trial_date)}</div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="mb-0">
                            <label className="form-label fw-bold mb-0">Due Date:</label>
                            <div className="form-control-plaintext">{formatDate(data.due_date)}</div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-4">
                        <div className="mb-0">
                            <label className="form-label fw-bold mb-0">Customer Code:</label>
                            <div className="form-control-plaintext">{data.customer?.label || 'N/A'}</div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="mb-0">
                            <label className="form-label fw-bold mb-0">Customer Name:</label>
                            <div className="form-control-plaintext">{getTranslatedText(data.customer_name)}</div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="mb-0">
                            <label className="form-label fw-bold mb-0">Phone Number:</label>
                            <div className="form-control-plaintext">{data.phone_number || 'N/A'}</div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-4">
                        <div className="mb-0">
                            <label className="form-label fw-bold mb-0">Address 1:</label>
                            <div className="form-control-plaintext">{getTranslatedText(data.address1)}</div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="mb-0">
                            <label className="form-label fw-bold mb-0">Address 2:</label>
                            <div className="form-control-plaintext">{data.address2 || 'N/A'}</div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="mb-0">
                            <label className="form-label fw-bold mb-0">Country:</label>
                            <div className="form-control-plaintext">{data.country?.label || 'N/A'}</div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-4">
                        <div className="mb-0">
                            <label className="form-label fw-bold mb-0">VAT Number:</label>
                            <div className="form-control-plaintext">{data.vat_number || 'N/A'}</div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="mb-0">
                            <label className="form-label fw-bold mb-0">VAT Group:</label>
                            <div className="form-control-plaintext">{data.vat_group?.label || 'N/A'}</div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="mb-0">
                            <label className="form-label fw-bold mb-0">Is Domestic:</label>
                            <div className="form-control-plaintext">{data.is_domestic ? 'Yes' : 'No'}</div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-12">
                        <div className="mb-0">
                            <label className="form-label fw-bold mb-0">Detailed Style Information:</label>
                            <div className="form-control-plaintext">{getTranslatedText(data.order_detail_info)}</div>
                        </div>
                    </div>
                </div>

                {data.lpo_pdf && (
                    <div className="row">
                        <div className="col-md-4">
                            <div className="mb-0">
                                <label className="form-label fw-bold mb-0">LPO PDF:</label>
                                <div className="form-control-plaintext">
                                    <a href={`${joborderlpoService.baseUrl}/file/${data.lpo_pdf}`} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
                                        <i className="fas fa-file-pdf me-2"></i>View PDF
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="row">
                    <Tab.Container activeKey={activeTab} onSelect={(key) => setActiveTab(key)}>
                        <Nav variant="tabs" className="mb-3">
                            <Nav.Item className="custom-li-trouser mb-0 p-0">
                                <Nav.Link eventKey="trouser">Trouser</Nav.Link>
                            </Nav.Item>
                            <Nav.Item className="custom-li-shirt mb-0 p-0">
                                <Nav.Link eventKey="jacket">Shirt / Jacket</Nav.Link>
                            </Nav.Item>
                            <Nav.Item className="custom-li-item mb-0 p-0">
                                <Nav.Link eventKey="items">Bill Items</Nav.Link>
                            </Nav.Item>
                        </Nav>
                        <Tab.Content>
                            <Tab.Pane eventKey="trouser" className="p-0">
                                <div className="row">
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/trouser/Around Waist.png`} alt="Around Waist" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Around Waist</label>
                                            <div className="form-control-plaintext text-center">{data.trouser?.waist_measurement || '0.00'} inch</div>
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/trouser/Around Hip.png`} alt="Around Hip" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Around Hip</label>
                                            <div className="form-control-plaintext text-center">{data.trouser?.hip_measurement || '0.00'} inch</div>
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/trouser/Waist Line To Heel Base Of Shoe-Length.png`} alt="Waist Line To Heel Base Of Shoe-Length" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Waist Line To Heel Base Of Shoe-Length</label>
                                            <div className="form-control-plaintext text-center">{data.trouser?.waist_line_to_heel || '0.00'} inch</div>
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/trouser/Aound Thigh Or Both Crease Of Trouser-Thigh.png`} alt="Around Thigh Or Both Crease Of Trouser-Thigh" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Around Thigh Or Both Crease Of Trouser-Thigh</label>
                                            <div className="form-control-plaintext text-center">{data.trouser?.thigh_measurement || '0.00'} inch</div>
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/trouser/Around Knees Or Both Crease Of Trouser Knee.png`} alt="Around Knees Or Both Crease Of Trouser Knee" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Around Knees Or Both Crease Of Trouser Knee</label>
                                            <div className="form-control-plaintext text-center">{data.trouser?.knee_measurement || '0.00'} inch</div>
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/trouser/Around Bottom Or Both Crease Of Trouser.png`} alt="Around Bottom Or Both Crease Of Trouser" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Around Bottom Or Both Crease Of Trouser</label>
                                            <div className="form-control-plaintext text-center">{data.trouser?.bottom_measurement || '0.00'} inch</div>
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/trouser/Waist Line To Crotch Point Rise Length.png`} alt="Waist Line To Crotch Point Rise Length" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Waist Line To Crotch Point Rise Length</label>
                                            <div className="form-control-plaintext text-center">{data.trouser?.waist_to_crotch || '0.00'} inch</div>
                                        </div>
                                    </div>

                                    <div className='col col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4'>
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/trouser/Erract Narrow Back Head Forward Flat Chest.png`} alt="Erract Narrow Back Head Forward Flat Chest" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Erract Narrow Back Head Forward Flat Chest</label>
                                            <div className="form-control-plaintext text-center">
                                                {data.trouser?.back_condition === 'narrow_back' ? '✓' : ''}
                                            </div>
                                        </div>
                                    </div>
                                    <div className='col col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4'>
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/trouser/Round Back Head Forward Flat Chest.png`} alt="Round Back Head Forward Flat Chest" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Round Back Head Forward Flat Chest</label>
                                            <div className="form-control-plaintext text-center">
                                                {data.trouser?.back_condition === 'round_back' ? '✓' : ''}
                                            </div>
                                        </div>
                                    </div>
                                    <div className='col col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4'>
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/trouser/Full Chest and Stomach Hollow Back.png`} alt="Full Chest and Stomach Hollow Back" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Full Chest and Stomach Hollow Back</label>
                                            <div className="form-control-plaintext text-center">
                                                {data.trouser?.back_condition === 'full_chest' ? '✓' : ''}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Tab.Pane>
                            <Tab.Pane eventKey="jacket" className="p-0">
                                <div className="row justify-content-center">
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/jacket/Shoulder Seam To First Bone.png`} alt="Shoulder Seam To First Bone" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Shoulder Seam To First Bone</label>
                                            <div className="form-control-plaintext text-center">{data.jacket?.shoulder_seam_to_first_bone || '0.00'} inch</div>
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/jacket/Around Chest.png`} alt="Around Chest" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Around Chest</label>
                                            <div className="form-control-plaintext text-center">{data.jacket?.around_chest || '0.00'} inch</div>
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/jacket/Around Waist 2.png`} alt="Around Waist" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Around Waist</label>
                                            <div className="form-control-plaintext text-center">{data.jacket?.around_waist || '0.00'} inch</div>
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/jacket/Aound Hip 2.png`} alt="Around Hip" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Around Hip</label>
                                            <div className="form-control-plaintext text-center">{data.jacket?.around_hip || '0.00'} inch</div>
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/jacket/Tip To Tip Of Shoulder.png`} alt="Tip To Tip Of Shoulder" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Tip To Tip Of Shoulder</label>
                                            <div className="form-control-plaintext text-center">{data.jacket?.tip_to_tip_shoulder || '0.00'} inch</div>
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/jacket/Center Of Back To Sleeve Seam.png`} alt="Center Of Back To Sleeve Seam" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Center Of Back To Sleeve Seam</label>
                                            <div className="form-control-plaintext text-center">{data.jacket?.center_back_sleeve_seam || '0.00'} inch</div>
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/jacket/Shoulder Tip To Below Wrist Bone.png`} alt="Shoulder Tip To Below Wrist Bone" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Shoulder Tip To Below Wrist Bone</label>
                                            <div className="form-control-plaintext text-center">{data.jacket?.shoulder_tip_to_below_wrist || '0.00'} inch</div>
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/jacket/Color Seam To Natural Waist.png`} alt="Color Seam To Natural Waist" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Color Seam To Natural Waist</label>
                                            <div className="form-control-plaintext text-center">{data.jacket?.color_seam_natural_waist || '0.00'} inch</div>
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/jacket/Shoulder Seam To Mid Rise.png`} alt="Shoulder Seam To Mid Rise" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Shoulder Seam To Mid Rise</label>
                                            <div className="form-control-plaintext text-center">{data.jacket?.shoulder_seam_mid_rise || '0.00'} inch</div>
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/jacket/Sleeve Length Tip Of Shoulder To2.png`} alt="Sleeve Length Tip Of Shoulder To" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Sleeve Length Tip Of Shoulder To</label>
                                            <div className="form-control-plaintext text-center">{data.jacket?.sleeve_length || '0.00'} inch</div>
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/jacket/Neck Around Neck2.png`} alt="Neck Around Neck" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Neck Around Neck</label>
                                            <div className="form-control-plaintext text-center">{data.jacket?.neck_around || '0.00'} inch</div>
                                        </div>
                                    </div>
                                </div>
                                <div className='row'>
                                    <div className='col-xl-12 col-xxl-6'>
                                        <div className='row justify-content-center'>
                                            <div className="col-12 text-center mb-4">
                                                <h4 className="fs-6 position-relative d-inline-block pb-2" style={{borderBottom: "2px solid #405189",letterSpacing: "1px"}}>
                                                    Shoulder Type
                                                </h4>
                                            </div>

                                            <div className='col col-sm-4 col-md-3 col-xxl-4 mb-4'>
                                                <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                                    <img src={`${process.env.PUBLIC_URL}/images/jacket/Sloping Should Long Neck.png`} alt="Sloping Should Long Neck" className="img-fluid"/>
                                                    <label className="fs-12-text mb-2 text-dark">Sloping Should Long Neck</label>
                                                    <div className="form-control-plaintext text-center">
                                                        {data.jacket?.shoulder_type === 'long_neck' ? '✓' : ''}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className='col col-sm-4 col-md-3 col-xxl-4 mb-4'>
                                                <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                                    <img src={`${process.env.PUBLIC_URL}/images/jacket/Sloping Regular Shoulder.png`} alt="Sloping Regular Shoulder" className="img-fluid"/>
                                                    <label className="fs-12-text mb-2 text-dark">Sloping Regular Shoulder</label>
                                                    <div className="form-control-plaintext text-center">
                                                        {data.jacket?.shoulder_type === 'regular_shoulder' ? '✓' : ''}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className='col col-sm-4 col-md-3 col-xxl-4 mb-4'>
                                                <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                                    <img src={`${process.env.PUBLIC_URL}/images/jacket/Square Shoulder Short Neck.png`} alt="Square Shoulder" className="img-fluid"/>
                                                    <label className="fs-12-text mb-2 text-dark">Square Shoulder</label>
                                                    <div className="form-control-plaintext text-center">
                                                        {data.jacket?.shoulder_type === 'square_shoulder' ? '✓' : ''}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className='col-xl-12 col-xxl-6'>
                                        <div className='row justify-content-center'>
                                            <div className="col-12 text-center mb-4">
                                                <h4 className="fs-6 position-relative d-inline-block pb-2" style={{borderBottom: "2px solid #405189",letterSpacing: "1px"}}>
                                                    Chest Type
                                                </h4>
                                            </div>

                                            <div className='col col-sm-4 col-md-3 col-xxl-4 mb-4'>
                                                <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                                    <img src={`${process.env.PUBLIC_URL}/images/jacket/Erract Narrow Back Proment Chest2.png`} alt="Erract Narrow Back Proment Chest" className="img-fluid"/>
                                                    <label className="fs-12-text mb-2 text-dark">Erract Narrow Back Proment Chest</label>
                                                    <div className="form-control-plaintext text-center">
                                                        {data.jacket?.chest_type === 'narrow_back_proment' ? '✓' : ''}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className='col col-sm-4 col-md-3 col-xxl-4 mb-4'>
                                                <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                                    <img src={`${process.env.PUBLIC_URL}/images/jacket/Round Back Head Forward Flat Chest2.png`} alt="Round Back Head Forward Flat Chest" className="img-fluid"/>
                                                    <label className="fs-12-text mb-2 text-dark">Round Back Head Forward Flat Chest</label>
                                                    <div className="form-control-plaintext text-center">
                                                        {data.jacket?.chest_type === 'forward_flat_chest' ? '✓' : ''}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className='col col-sm-4 col-md-3 col-xxl-4 mb-4'>
                                                <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                                    <img src={`${process.env.PUBLIC_URL}/images/jacket/Full Chest And Stomach Hollow Back2.png`} alt="Full Chest And Stomach Hollow Back" className="img-fluid"/>
                                                    <label className="fs-12-text mb-2 text-dark">Full Chest And Stomach Hollow Back</label>
                                                    <div className="form-control-plaintext text-center">
                                                        {data.jacket?.chest_type === 'stomach_hollow_back' ? '✓' : ''}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Tab.Pane>
                            <Tab.Pane eventKey="items" className="p-0">
                                <div className="table-responsive mt-4">
                                    <table className="table table-bordered">
                                        <thead>
                                            <tr>
                                                <th className="text-nowrap p-3">Item Code</th>
                                                <th className="text-nowrap p-3">Item Name</th>
                                                <th className="text-nowrap p-3">Unit</th>
                                                <th className="text-nowrap p-3">Qty</th>
                                                <th className="text-nowrap p-3">Unit Price</th>
                                                <th className="text-nowrap p-3">Discount %</th>
                                                <th className="text-nowrap p-3">Line Amount</th>
                                                <th className="text-nowrap p-3">Header Discount</th>
                                                <th className="text-nowrap p-3">Amt Excl VAT</th>
                                                <th className="text-nowrap p-3">VAT Amount</th>
                                                <th className="text-nowrap p-3">Amt Incl VAT</th>
                                                <th className="text-nowrap p-3">Spl. Item Description</th>
                                                <th className="text-nowrap p-3">VAT Product Group</th>
                                                <th className="text-nowrap p-3">VAT %</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.items && data.items.length > 0 ? (
                                                data.items.map((item, index) => (
                                                    <tr key={index}>
                                                        <td className="text-nowrap p-3 align-middle">{item.item?.item_code || 'N/A'}</td>
                                                        <td className="text-nowrap p-3 align-middle">{item.item?.label || 'N/A'}</td>
                                                        <td className="p-3 align-middle">{item.unit || 'N/A'}</td>
                                                        <td className="p-3 align-middle">{item.quantity || '0'}</td>
                                                        <td className="p-3 align-middle">{formatCurrency(item.unit_price)}</td>
                                                        <td className="p-3 align-middle">{item.discount_percentage || '0.00'}%</td>
                                                        <td className="p-3 align-middle">{formatCurrency(item.line_amount)}</td>
                                                        <td className="p-3 align-middle">{formatCurrency(item.header_discount)}</td>
                                                        <td className="p-3 align-middle">{formatCurrency(item.amount_excl_vat)}</td>
                                                        <td className="p-3 align-middle">{formatCurrency(item.vat_amount)}</td>
                                                        <td className="p-3 align-middle">{formatCurrency(item.amount_incl_vat)}</td>
                                                        <td className="p-3 align-middle">{item.item_description || 'N/A'}</td>
                                                        <td className="p-3 align-middle">{item.vat_product_group?.label || 'N/A'}</td>
                                                        <td className="p-3 align-middle">{item.vat_percentage || '0.00'}%</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="14" className="text-center">No items added</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="row mt-4 d-flex justify-content-end">
                                    <div className="col-md-4">
                                        <div className="mb-2 row">
                                            <label className="col-lg-6 col-md-6 col-form-label fw-bold">Gross Total:</label>
                                            <div className="col-lg-6 col-md-6">
                                                <div className="form-control-plaintext text-end">{formatCurrency(data.gross_total)}</div>
                                            </div>
                                        </div>
                                        <div className="mb-2 row d-none">
                                            <label className="col-lg-6 col-md-6 col-form-label fw-bold">Discount %:</label>
                                            <div className="col-lg-6 col-md-6">
                                                <div className="form-control-plaintext text-end">{data.discount_percentage || '0.00'}%</div>
                                            </div>
                                        </div>
                                        <div className="mb-2 row d-none">
                                            <label className="col-lg-6 col-md-6 col-form-label fw-bold">Discount Amt:</label>
                                            <div className="col-lg-6 col-md-6">
                                                <div className="form-control-plaintext text-end">{formatCurrency(data.discount_amount)}</div>
                                            </div>
                                        </div>
                                        <div className="mb-2 row">
                                            <label className="col-lg-6 col-md-6 col-form-label fw-bold">Sub Total:</label>
                                            <div className="col-lg-6 col-md-6">
                                                <div className="form-control-plaintext text-end">{formatCurrency(data.sub_total)}</div>
                                            </div>
                                        </div>
                                        <div className="mb-2 row">
                                            <label className="col-lg-6 col-md-6 col-form-label fw-bold">VAT Amt:</label>
                                            <div className="col-lg-6 col-md-6">
                                                <div className="form-control-plaintext text-end">{formatCurrency(data.vat_amount)}</div>
                                            </div>
                                        </div>
                                        <div className="mb-2 row">
                                            <label className="col-lg-6 col-md-6 col-form-label fw-bold">Net Total:</label>
                                            <div className="col-lg-6 col-md-6">
                                                <div className="form-control-plaintext text-end">{formatCurrency(data.net_total)}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Tab.Pane>
                        </Tab.Content>
                    </Tab.Container>
                </div>
            </div>
            
            {/* Print Modal */}
            {showPrintModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 9999,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        width: '90%',
                        height: '90%',
                        borderRadius: '8px',
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            padding: '15px',
                            borderBottom: '1px solid #ddd',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <h5 style={{ margin: 0 }}>Print Preview</h5>
                            <div>
                                <button 
                                    className="btn btn-primary btn-sm me-2"
                                    onClick={() => {
                                        const printWindow = window.open('', '_blank');
                                        if (!printWindow) return;
                                        const baseUrl = window.location.origin + (process.env.PUBLIC_URL || '');
                                        const cssHref = `${baseUrl}/css/job_order_print.css`;
                                        const doPrint = () => {
                                            const modalContent = document.querySelector('.modal-print-content');
                                            if (modalContent) {
                                                const printContent = modalContent.innerHTML;
                                                const target = printWindow.document.getElementById('print-content');
                                                if (target) target.innerHTML = printContent;
                                            }
                                            printWindow.document.close();
                                            setTimeout(() => {
                                                printWindow.print();
                                                printWindow.close();
                                            }, 100);
                                        };
                                        printWindow.document.write(`
                                            <html>
                                                <head>
                                                    <title>Job Order LPO Print</title>
                                                    <link rel="stylesheet" href="${cssHref}" id="print-css-link" />
                                                </head>
                                                <body>
                                                    <div id="print-content"></div>
                                                </body>
                                            </html>
                                        `);
                                        const linkEl = printWindow.document.getElementById('print-css-link');
                                        if (linkEl) {
                                            const fallback = setTimeout(doPrint, 3000);
                                            const done = () => { clearTimeout(fallback); doPrint(); };
                                            linkEl.onload = done;
                                            linkEl.onerror = done;
                                            if (linkEl.sheet) done();
                                        } else {
                                            doPrint();
                                        }
                                    }}
                                >
                                    <i className="fas fa-print me-1"></i>
                                    Print
                                </button>
                                <button 
                                    className="btn btn-secondary btn-sm"
                                    onClick={handleClosePrintModal}
                                >
                                    <i className="fas fa-times me-1"></i>
                                    Close
                                </button>
                            </div>
                        </div>
                        <div style={{
                            height: 'calc(100% - 70px)',
                            overflow: 'auto',
                            padding: '20px'
                        }} className="modal-print-content">
                            <JobOrderLpoPrint data={data} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default JobOrderLpoPreview;
